/**
 * Servicio de datos agregados para la página /impuestos.
 * Combina datos de catalogo_impuestos, impuestos_cliente y vencimientos_fiscales
 * para ofrecer métricas consolidadas.
 *
 * Requiere cliente autenticado de Supabase como primer parámetro.
 *
 * @example
 * const { supabase } = useSupabase()
 * const catalogo = await impuestosPageService.obtenerCatalogoConMetricas(supabase!, orgId)
 * const stats    = await impuestosPageService.obtenerEstadisticas(supabase!, orgId)
 */

import { format, startOfMonth, endOfMonth, startOfDay } from 'date-fns'
import type { SupabaseClient } from '@supabase/supabase-js'
import type { Database } from '@/types/database'

// ===========================================
// Tipos públicos exportados
// ===========================================

export interface CatalogoConMetricas {
  id: string
  codigo: string
  nombre: string
  nombre_corto: string | null
  categoria: string
  jurisdiccion: string | null
  color: string | null
  orden: number | null
  activo: boolean | null
  is_global: boolean
  organizacion_id: string | null
  clientes_asignados: number
  vencimientos_pendientes: number
  vencimientos_vencidos: number
  proximo_vencimiento: string | null
}

export interface ImpuestosPageStats {
  total_impuestos: number
  total_clientes_con_impuestos: number
  pendientes_este_mes: number
  vencidos: number
}

/** Impuesto activo con clientes asignados que dejó de generar vencimientos (ORR-268) */
export type HuecoVencimiento =
  Database['public']['Functions']['detectar_huecos_vencimientos']['Returns'][number]

// -----------------------------------------
// Tipos internos para queries
// -----------------------------------------

interface CatalogoRow {
  id: string
  codigo: string
  nombre: string
  nombre_corto: string | null
  categoria: string
  jurisdiccion: string | null
  color: string | null
  orden: number | null
  activo: boolean | null
  is_global?: boolean
  organizacion_id?: string | null
}

interface ImpuestosClienteRow {
  impuesto_id: string
  cliente_id: string
}

interface VencimientoRow {
  impuesto_id: string | null
  estado_efectivo: string | null
  fecha_vencimiento: string
}

interface ClienteConImpuesto {
  nombre: string | null
  cuit: string | null
  impuesto_id: string
  numero_inscripcion: string | null
  fecha_inscripcion: string | null
  alicuota: number | null
  regimen: string | null
  activo: boolean | null
}

interface VencimientoReciente {
  id: string
  periodo: string
  fecha_vencimiento: string
  estado_efectivo: string | null
  presentado: boolean | null
  pagado: boolean | null
  cliente_id: string | null
}

// ===========================================
// Implementaciones de métodos
// ===========================================

/**
 * Obtiene todos los impuestos activos del catálogo con métricas calculadas
 * para mostrar en la tabla principal de la página /impuestos.
 *
 * Usa 4 queries paralelas para evitar N+1 y luego fusiona en memoria.
 */
async function obtenerCatalogoConMetricas(
  client: SupabaseClient<Database>,
  orgId: string
): Promise<CatalogoConMetricas[]> {
  // Query 1: Catálogo activo (globales + per-org)
  const catalogoPromise = client
    .from('catalogo_impuestos')
    .select('*')
    .or(`organizacion_id.is.null,organizacion_id.eq.${orgId}`)
    .eq('activo', true)
    .order('orden', { ascending: true })
    .order('nombre', { ascending: true })

  // Query 2: Conteo de clientes asignados por impuesto (filtered by org)
  const clientesPromise = client
    .from('impuestos_cliente')
    .select('impuesto_id, cliente_id, cliente:clientes!cliente_id!inner(organizacion_id)')
    .eq('activo', true)
    .eq('cliente.organizacion_id', orgId)

  // Query 3: Vencimientos pendientes y vencidos por impuesto (vista incluye virtuales del calendario AFIP)
  const vencimientosPromise = client
    .from('vista_proximos_vencimientos')
    .select('impuesto_id, estado_efectivo, fecha_vencimiento')
    .in('estado_efectivo', ['pendiente', 'vencido'])

  const [catalogoResult, clientesResult, vencimientosResult] = await Promise.all([
    catalogoPromise,
    clientesPromise,
    vencimientosPromise,
  ])

  if (catalogoResult.error) throw catalogoResult.error
  if (clientesResult.error) throw clientesResult.error
  if (vencimientosResult.error) throw vencimientosResult.error

  const catalogoRaw = (catalogoResult.data ?? []) as unknown as CatalogoRow[]
  const clientesData = (clientesResult.data ?? []) as ImpuestosClienteRow[]
  const vencimientosData = (vencimientosResult.data ?? []) as VencimientoRow[]

  // Dedup by codigo: per-org (has organizacion_id) wins over global
  const mergedMap = new Map<string, CatalogoRow>()
  for (const imp of catalogoRaw) {
    const existing = mergedMap.get(imp.codigo)
    if (!existing || (imp.organizacion_id && !existing.organizacion_id)) {
      mergedMap.set(imp.codigo, imp)
    }
  }
  const catalogoData = Array.from(mergedMap.values())

  const hoy = format(startOfDay(new Date()), 'yyyy-MM-dd')

  // Indexar en Maps para O(1) lookups
  const clientesPorImpuesto = new Map<string, Set<string>>()
  for (const ic of clientesData) {
    const set = clientesPorImpuesto.get(ic.impuesto_id) ?? new Set()
    set.add(ic.cliente_id)
    clientesPorImpuesto.set(ic.impuesto_id, set)
  }

  const pendientesPorImpuesto = new Map<string, number>()
  const vencidosPorImpuesto = new Map<string, number>()
  const proximoPorImpuesto = new Map<string, string>()

  for (const v of vencimientosData) {
    if (!v.impuesto_id) continue

    const esPendiente = v.estado_efectivo === 'pendiente' && v.fecha_vencimiento >= hoy
    const esVencido = v.estado_efectivo === 'vencido'

    if (esPendiente) {
      pendientesPorImpuesto.set(
        v.impuesto_id,
        (pendientesPorImpuesto.get(v.impuesto_id) ?? 0) + 1
      )

      // Mantener el próximo vencimiento (fecha más temprana)
      const actual = proximoPorImpuesto.get(v.impuesto_id)
      if (!actual || v.fecha_vencimiento < actual) {
        proximoPorImpuesto.set(v.impuesto_id, v.fecha_vencimiento)
      }
    }

    if (esVencido) {
      vencidosPorImpuesto.set(
        v.impuesto_id,
        (vencidosPorImpuesto.get(v.impuesto_id) ?? 0) + 1
      )
    }
  }

  // Fusionar catálogo con métricas calculadas
  return catalogoData.map((impuesto) => ({
    id: impuesto.id,
    codigo: impuesto.codigo,
    nombre: impuesto.nombre,
    nombre_corto: impuesto.nombre_corto,
    categoria: impuesto.categoria,
    jurisdiccion: impuesto.jurisdiccion,
    color: impuesto.color,
    orden: impuesto.orden,
    activo: impuesto.activo,
    is_global: impuesto.is_global ?? false,
    organizacion_id: impuesto.organizacion_id ?? null,
    clientes_asignados: clientesPorImpuesto.get(impuesto.id)?.size ?? 0,
    vencimientos_pendientes: pendientesPorImpuesto.get(impuesto.id) ?? 0,
    vencimientos_vencidos: vencidosPorImpuesto.get(impuesto.id) ?? 0,
    proximo_vencimiento: proximoPorImpuesto.get(impuesto.id) ?? null,
  }))
}

/**
 * Calcula estadísticas de alto nivel para los KPI cards de la página /impuestos.
 */
async function obtenerEstadisticas(
  client: SupabaseClient<Database>,
  orgId: string
): Promise<ImpuestosPageStats> {
  const ahora = new Date()
  const inicioMes = format(startOfMonth(ahora), 'yyyy-MM-dd')
  const finMes = format(endOfMonth(ahora), 'yyyy-MM-dd')

  // Query 1: Total de impuestos activos en el catálogo (globales + per-org)
  const totalImpuestosPromise = client
    .from('catalogo_impuestos')
    .select('id', { count: 'exact', head: true })
    .or(`organizacion_id.is.null,organizacion_id.eq.${orgId}`)
    .eq('activo', true)

  // Query 2: Clientes distintos con al menos un impuesto activo (filtered by org)
  // Se deduplicarán en memoria con Set (PostgREST no soporta COUNT DISTINCT)
  const clientesConImpuestosPromise = client
    .from('impuestos_cliente')
    .select('cliente_id, cliente:clientes!cliente_id!inner(organizacion_id)')
    .eq('activo', true)
    .eq('cliente.organizacion_id', orgId)

  // Query 3: Vencimientos pendientes este mes (vista incluye virtuales)
  const pendientesEsteMesPromise = client
    .from('vista_proximos_vencimientos')
    .select('id', { count: 'exact', head: true })
    .eq('organizacion_id', orgId)
    .eq('estado_efectivo', 'pendiente')
    .gte('fecha_vencimiento', inicioMes)
    .lte('fecha_vencimiento', finMes)

  // Query 4: Vencimientos vencidos (estado_efectivo ya calcula fecha pasada)
  const vencidosPromise = client
    .from('vista_proximos_vencimientos')
    .select('id', { count: 'exact', head: true })
    .eq('organizacion_id', orgId)
    .eq('estado_efectivo', 'vencido')

  const [
    totalImpuestosResult,
    clientesConImpuestosResult,
    pendientesEsteMesResult,
    vencidosResult,
  ] = await Promise.all([
    totalImpuestosPromise,
    clientesConImpuestosPromise,
    pendientesEsteMesPromise,
    vencidosPromise,
  ])

  if (totalImpuestosResult.error) throw totalImpuestosResult.error
  if (clientesConImpuestosResult.error) throw clientesConImpuestosResult.error
  if (pendientesEsteMesResult.error) throw pendientesEsteMesResult.error
  if (vencidosResult.error) throw vencidosResult.error

  const clientesDistintos = new Set(
    (clientesConImpuestosResult.data ?? []).map((r) => r.cliente_id)
  )

  return {
    total_impuestos: totalImpuestosResult.count ?? 0,
    total_clientes_con_impuestos: clientesDistintos.size,
    pendientes_este_mes: pendientesEsteMesResult.count ?? 0,
    vencidos: vencidosResult.count ?? 0,
  }
}

/**
 * Obtiene detalle completo de un impuesto: clientes asignados y vencimientos
 * recientes.
 */
async function obtenerDetalleImpuesto(
  client: SupabaseClient<Database>,
  impuestoId: string,
  orgId: string
): Promise<{
  clientes: ClienteConImpuesto[]
  vencimientos: VencimientoReciente[]
}> {
  // Query 1: Clientes asignados a este impuesto con datos del cliente (filtered by org)
  const clientesPromise = client
    .from('impuestos_cliente')
    .select(`
      impuesto_id,
      numero_inscripcion,
      fecha_inscripcion,
      alicuota,
      regimen,
      activo,
      cliente:clientes!cliente_id!inner(id, organizacion_id, persona:personas!persona_id(nombre_completo, razon_social, cuit, tipo))
    `)
    .eq('impuesto_id', impuestoId)
    .eq('activo', true)
    .eq('cliente.organizacion_id', orgId)
    .order('created_at', { ascending: true })

  // Query 2: Vencimientos próximos para este impuesto (vista incluye virtuales)
  const vencimientosPromise = client
    .from('vista_proximos_vencimientos')
    .select('id, periodo, fecha_vencimiento, estado_efectivo, presentado, pagado, cliente_id')
    .eq('impuesto_id', impuestoId)
    .order('fecha_vencimiento', { ascending: true })
    .limit(20)

  const [clientesResult, vencimientosResult] = await Promise.all([
    clientesPromise,
    vencimientosPromise,
  ])

  if (clientesResult.error) throw clientesResult.error
  if (vencimientosResult.error) throw vencimientosResult.error

  // Normalizar shape del join de clientes → personas
  const clientes: ClienteConImpuesto[] = (clientesResult.data ?? []).map((row) => {
    const clienteData = Array.isArray(row.cliente) ? row.cliente[0] : row.cliente
    const persona = clienteData?.persona
      ? (Array.isArray(clienteData.persona) ? clienteData.persona[0] : clienteData.persona)
      : null
    const nombre = persona
      ? (persona.tipo === 'juridica' ? persona.razon_social : persona.nombre_completo)
      : null
    return {
      impuesto_id: row.impuesto_id,
      numero_inscripcion: row.numero_inscripcion,
      fecha_inscripcion: row.fecha_inscripcion,
      alicuota: row.alicuota,
      regimen: row.regimen,
      activo: row.activo,
      nombre: nombre ?? null,
      cuit: persona?.cuit ?? null,
    }
  })

  return {
    clientes,
    vencimientos: (vencimientosResult.data ?? []) as VencimientoReciente[],
  }
}

// ===========================================
// Posición fiscal IVA multi-período
// ===========================================

interface RPCResumenRow {
  tipo: string
  total_exento: number
  total_final: number
  total_gravado: number
  total_iva: number
  total_no_gravado: number
  total_percepciones: number
}

export interface ResumenIVAMes {
  periodo: string
  debito_fiscal: number
  credito_fiscal: number
  percepciones: number
  saldo: number
  total_ventas: number
  total_compras: number
}

export interface PosicionFiscalIVA {
  meses: ResumenIVAMes[]
  totales: {
    debito_fiscal: number
    credito_fiscal: number
    percepciones: number
    saldo: number
    total_ventas: number
    total_compras: number
  }
}

async function obtenerPosicionFiscalIVA(
  client: SupabaseClient<Database>,
  orgId: string,
  anio: number
): Promise<PosicionFiscalIVA> {
  // Call RPC once per month in parallel (12 calls)
  // RPC expects date type, not period string — use first/last day of month
  const promises = Array.from({ length: 12 }, (_, i) => {
    const mes = String(i + 1).padStart(2, '0')
    const periodo = `${anio}-${mes}`
    const primerDia = `${anio}-${mes}-01`
    const ultimoDia = format(endOfMonth(new Date(anio, i, 1)), 'yyyy-MM-dd')
    return client
      .rpc('resumen_iva_periodo', {
        p_organizacion_id: orgId,
        p_periodo_desde: primerDia,
        p_periodo_hasta: ultimoDia,
      })
      .then((res) => ({ periodo, data: res.data, error: res.error }))
  })

  const results = await Promise.all(promises)

  const meses: ResumenIVAMes[] = []
  let totalDebito = 0
  let totalCredito = 0
  let totalPercepciones = 0
  let totalVentas = 0
  let totalCompras = 0

  for (const { periodo, data, error } of results) {
    if (error) throw error

    const rows = (data || []) as RPCResumenRow[]
    const ventasRow = rows.find((r) => r.tipo.toUpperCase() === 'VENTAS')
    const comprasRow = rows.find((r) => r.tipo.toUpperCase() === 'COMPRAS')

    const debito = ventasRow?.total_iva ?? 0
    const credito = comprasRow?.total_iva ?? 0
    const percepciones = (comprasRow?.total_percepciones ?? 0) + (ventasRow?.total_percepciones ?? 0)

    meses.push({
      periodo,
      debito_fiscal: debito,
      credito_fiscal: credito,
      percepciones,
      saldo: debito - credito - percepciones,
      total_ventas: ventasRow?.total_final ?? 0,
      total_compras: comprasRow?.total_final ?? 0,
    })

    totalDebito += debito
    totalCredito += credito
    totalPercepciones += percepciones
    totalVentas += ventasRow?.total_final ?? 0
    totalCompras += comprasRow?.total_final ?? 0
  }

  return {
    meses,
    totales: {
      debito_fiscal: totalDebito,
      credito_fiscal: totalCredito,
      percepciones: totalPercepciones,
      saldo: totalDebito - totalCredito - totalPercepciones,
      total_ventas: totalVentas,
      total_compras: totalCompras,
    },
  }
}

/**
 * Impuestos activos con clientes asignados que no tienen vencimientos más allá de `hasta`.
 * ORR-268: sin esto, un impuesto puede quedarse sin generar y nadie se entera (caso CM-05).
 * Los conceptos que no presentan DDJJ se excluyen marcando `genera_vencimientos = false`.
 */
async function detectarHuecosVencimientos(
  client: SupabaseClient<Database>,
  orgId: string,
  hasta?: string
): Promise<HuecoVencimiento[]> {
  const { data, error } = await client.rpc('detectar_huecos_vencimientos', {
    p_organizacion_id: orgId,
    ...(hasta ? { p_hasta: hasta } : {}),
  })

  if (error) throw error
  return data ?? []
}

// ===========================================
// Objeto de servicio exportado
// ===========================================

export const impuestosPageService = {
  obtenerCatalogoConMetricas,
  obtenerEstadisticas,
  obtenerDetalleImpuesto,
  obtenerPosicionFiscalIVA,
  detectarHuecosVencimientos,
}
